import { MapTypeId, MapTypeStyle } from 'bits/map'

interface MarkerColorsHash {
  stroke: string
  fill: Record<'default' | 'red' | 'orange' | 'green' | 'blue' | 'purple', string>
}

interface MapTheme {
  styles?: MapTypeStyle[]
  mapTypeId?: string
  id: number
  markerColor: string // old
  markerColors: MarkerColorsHash
  previewImageUrl: string
  connectorLineColor?: string
  labelBackgroundColor?: string
  labelTextColor?: string
}

const GOOGLE_STATIC_MAPS_API_PREFIX =
  'https://maps.googleapis.com/maps/api/staticmap?key=AIzaSyD3kjetwpeAYF-eXThlRhc1F1EYwsQvHcY&center={LATITUDE},{LONGITUDE}&zoom={ZOOM}&format=png&size=960x720'
const createPreviewImageHash = (theme: MapTheme): string => {
  const styles = theme.styles || []
  const mapTypeId = theme.mapTypeId || 'roadmap'
  return (
    GOOGLE_STATIC_MAPS_API_PREFIX +
    '&' +
    styles
      .map((style): string => {
        const feature = style.featureType ? `feature:${style.featureType}` : null
        const element = style.elementType ? `element:${style.elementType}` : null
        const stylers =
          style.stylers && style.stylers.length > 0
            ? style.stylers
                .map((styler): string =>
                  Object.keys(styler)
                    .map((stylingKey): string => `${stylingKey}:${styler[stylingKey]}`)
                    .join('%7C'),
                )
                .join('%7C')
                .replace('#', '0x')
            : null
        const isNotNull = (x: any): boolean => !!x
        return 'style=' + [feature, element, stylers].filter(isNotNull).join('%7C') // encoded vertical separator |
      })
      .join('&') +
    '&maptype=' +
    mapTypeId
  )
}

const MARKER_FILL_SET_BRIGHT = {
  default: 'FF00F5', // #FF00F5
  red: 'FF3D00', // #FF3D00
  orange: 'FF8A00', // #FF8A00
  green: '7AC92B', // #7AC92B
  blue: '2292E2', // #2292E2
  purple: 'AB4CE5', // #AB4CE5
}

const MARKER_FILL_SET_ANTIQUE = {
  default: '212121', // #212121
  red: 'B64C46', // #B64C46
  orange: 'E39115', // #E39115
  green: '68A05F', // #68A05F
  blue: '436BAE', // #436BAE
  purple: '724596', // #724596
}

const MARKER_FILL_SET_MELLOW = {
  default: 'FF40D5', // #FF40D5
  red: 'E20C00', // #E20C00
  orange: 'FF8200', // #FF8200
  green: '42AC31', // #42AC31
  blue: '1F65D9', // #1F65D9
  purple: '9F01FF', // #9F01FF
}

// Default Apple-like Theme - https://snazzymaps.com/style/281616/our-apple-like-style
const THE_USUAL: MapTheme = {
  id: 100,
  previewImageUrl: '',
  markerColor: '#ff1000',
  markerColors: {
    stroke: 'fff', // #fff
    fill: MARKER_FILL_SET_BRIGHT,
  },
  connectorLineColor: '#000000',
  labelBackgroundColor: '#000000',
  labelTextColor: '#ffffff',
  styles: [
    {
      featureType: 'landscape.man_made',
      elementType: 'geometry',
      stylers: [
        {
          color: '#f7f1df',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry.fill',
      stylers: [
        {
          visibility: 'on',
        },
        {
          color: '#f7f1df',
        },
      ],
    },
    {
      featureType: 'landscape.natural.landcover',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#d0e3b4',
        },
        {
          visibility: 'simplified',
        },
      ],
    },
    {
      featureType: 'landscape.natural.terrain',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#c7dcab',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape.natural.terrain',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#c7dcab',
        },
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi.business',
      elementType: 'all',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi.medical',
      elementType: 'geometry',
      stylers: [
        {
          color: '#fbd3da',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#bde6ab',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffe15f',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#efd151',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'transit.station.airport',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#cfb2db',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#a2daf2',
        },
      ],
    },
  ],
}

// Google Silver - https://mapstyle.withgoogle.com
const SILVER: MapTheme = {
  id: 101,
  previewImageUrl: '',
  markerColor: '#000000',
  markerColors: {
    stroke: 'fff', // #fff
    fill: {
      ...MARKER_FILL_SET_BRIGHT,
      default: '212121', // #212121
    },
  },
  connectorLineColor: '#636363',
  labelBackgroundColor: '#636363',
  labelTextColor: '#FFFFFF',
  styles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#f5f5f5',
        },
      ],
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#f5f5f5',
        },
      ],
    },
    {
      featureType: 'administrative',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ffffff',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dadada',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'transit',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'water',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#c9c9c9',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
  ],
}

// Google Dark - https://mapstyle.withgoogle.com
const BLACK_METAL: MapTheme = {
  id: 102,
  previewImageUrl: '',
  markerColor: '#ffffff',
  markerColors: {
    stroke: '212121', // #212121
    fill: {
      ...MARKER_FILL_SET_MELLOW,
      default: 'FFF',
    },
  },
  connectorLineColor: '#ffffff',
  labelBackgroundColor: '#ffffff',
  labelTextColor: '#000000',
  styles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#212121',
        },
      ],
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#212121',
        },
      ],
    },
    {
      featureType: 'administrative',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'administrative.country',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd',
        },
      ],
    },
    {
      featureType: 'landscape',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#181818',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#1b1b1b',
        },
      ],
    },
    {
      featureType: 'road',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#2c2c2c',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#8a8a8a',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry',
      stylers: [
        {
          color: '#373737',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#3c3c3c',
        },
      ],
    },
    {
      featureType: 'road.highway.controlled_access',
      elementType: 'geometry',
      stylers: [
        {
          color: '#4e4e4e',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'transit',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'water',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#3d3d3d',
        },
      ],
    },
  ],
}

// Medieval Style - https://snazzymaps.com/style/272010/vintage
const VINTAGE: MapTheme = {
  id: 103,
  previewImageUrl: '',
  markerColor: '#000000', // #000000
  markerColors: {
    stroke: 'FFF', // #FFF
    fill: MARKER_FILL_SET_ANTIQUE,
  },
  connectorLineColor: '#FFFFFF',
  labelTextColor: '#000000',
  labelBackgroundColor: '#FFFFFF',
  styles: [
    {
      featureType: 'administrative',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#5d3806',
        },
        {
          weight: 1.5,
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#3e2304',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#c99e6d',
        },
        {
          visibility: 'simplified',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'all',
      stylers: [
        {
          lightness: -8,
        },
        {
          gamma: 0.98,
        },
        {
          weight: 2.45,
        },
        {
          saturation: 26,
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [
        {
          lightness: 30,
        },
        {
          saturation: 30,
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#3e2304',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'landscape.man_made',
      elementType: 'all',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape.man_made',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#d6b169',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#d7a663',
        },
      ],
    },
    {
      featureType: 'landscape.natural.landcover',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#d7a663',
        },
      ],
    },
    {
      featureType: 'landscape.natural.terrain',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#c69253',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'all',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'simplified',
        },
        {
          color: '#3e2304',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ca964c',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'all',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'all',
      stylers: [
        {
          lightness: -20,
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#f2e0bf',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#c9aa6f',
        },
        {
          visibility: 'on',
        },
      ],
    },
  ],
}

// Retro style - https://mapstyle.withgoogle.com
const RETRO: MapTheme = {
  id: 104,
  previewImageUrl: '',
  markerColor: '#000000', // #000000
  markerColors: {
    stroke: 'FFF', // #FFF
    fill: MARKER_FILL_SET_ANTIQUE,
  },
  connectorLineColor: '#FF6100',
  labelTextColor: '#FFFFFF',
  labelBackgroundColor: '#FF6100',
  styles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#ebe3cd',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#523735',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#f5f1e6',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#c9b2a6',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#dcd2be',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#ae9e90',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dfd2ae',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dfd2ae',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#93817c',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#a5b076',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#447530',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#f5f1e6',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry',
      stylers: [
        {
          color: '#fdfcf8',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#f8c967',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#e9bc62',
        },
      ],
    },
    {
      featureType: 'road.highway.controlled_access',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e98d58',
        },
      ],
    },
    {
      featureType: 'road.highway.controlled_access',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#db8555',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#806b63',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dfd2ae',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#8f7d77',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#ebe3cd',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dfd2ae',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#b9d3c2',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#92998d',
        },
      ],
    },
  ],
}

// Tron like with neon color features - https://snazzymaps.com/style/271969/tron
const TRON: MapTheme = {
  id: 105,
  previewImageUrl: '',
  markerColor: '#00dfff',
  markerColors: {
    stroke: '212121', // #212121
    fill: MARKER_FILL_SET_MELLOW,
  },
  connectorLineColor: '#FFF64A',
  labelTextColor: '#343358',
  labelBackgroundColor: '#FFF64A',
  styles: [
    {
      featureType: 'administrative',
      elementType: 'geometry',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#aeaeae',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#364d6d',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [
        {
          visibility: 'on',
        },
        {
          color: '#364d6d',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#070535',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#4b6282',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#364d6d',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#070535',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#4b6282',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#070535',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#4b6282',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#146474',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          lightness: 25,
        },
        {
          color: '#146474',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#7a4b82',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#4b6282',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'geometry.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#146474',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#146474',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels.text.fill',
      stylers: [
        {
          visibility: 'on',
        },
        {
          color: '#070535',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#4b6282',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels.icon',
      stylers: [
        {
          saturation: -30,
        },
        {
          lightness: -35,
        },
        {
          gamma: 1,
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#34335b',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#637895',
        },
        {
          visibility: 'on',
        },
      ],
    },
  ],
}

// Google Night Driving Style - https://mapstyle.withgoogle.com
const NIGHT: MapTheme = {
  id: 106,
  previewImageUrl: '',
  markerColor: '#ff9900',
  markerColors: {
    stroke: '212121', // #212121
    fill: MARKER_FILL_SET_MELLOW,
  },
  connectorLineColor: '#FFFFFF',
  labelTextColor: '#343358',
  labelBackgroundColor: '#FFFFFF',
  styles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#242f3e',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#746855',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#242f3e',
        },
      ],
    },
    {
      featureType: 'administrative',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'administrative.locality',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#d59563',
        },
      ],
    },
    {
      featureType: 'landscape',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#d59563',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#263c3f',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#6b9a76',
        },
      ],
    },
    {
      featureType: 'road',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#38414e',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#212a37',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9ca5b3',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#746855',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#1f2835',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#f3d19c',
        },
      ],
    },
    {
      featureType: 'transit',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'geometry',
      stylers: [
        {
          color: '#2f3948',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#d59563',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#17263c',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#515c6d',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#17263c',
        },
      ],
    },
  ],
}

// Colorful Amusement Park style - https://snazzymaps.com/style/272136/amusement-park
const AMUSEMENT_PARK: MapTheme = {
  id: 107,
  previewImageUrl: '',
  markerColor: '#ff0000', // #ff0000
  markerColors: {
    stroke: '000', // #000
    fill: MARKER_FILL_SET_BRIGHT,
  },
  connectorLineColor: '#000000',
  labelTextColor: '#FFFFFF',
  labelBackgroundColor: '#000000',
  styles: [
    {
      featureType: 'administrative',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'administrative.province',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#ff0000',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffec02',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffec02',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#000000',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          visibility: 'on',
        },
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#07e126',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ff89cc',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry.stroke',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ff8300',
        },
      ],
    },
    {
      featureType: 'transit.station.airport',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ffe900',
        },
      ],
    },
    {
      featureType: 'transit.station.bus',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ff8300',
        },
      ],
    },
    {
      featureType: 'transit.station.rail',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ff8300',
        },
      ],
    },
    {
      featureType: 'transit.station.rail',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#ff0000',
        },
      ],
    },
    {
      featureType: 'transit.station.rail',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'transit.station.rail',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#000000',
        },
      ],
    },
    {
      featureType: 'transit.station.rail',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'all',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#408cf5',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#004c8d',
        },
      ],
    },
  ],
}

// Video game, Minecraft inspired - https://snazzymaps.com/style/271959/minecraft
const VIDEO_GAME: MapTheme = {
  id: 108,
  previewImageUrl: '',
  markerColor: '#f09035',
  markerColors: {
    stroke: '212121', // #212121
    fill: MARKER_FILL_SET_MELLOW,
  },
  connectorLineColor: '#FFF64A',
  labelTextColor: '#000000',
  labelBackgroundColor: '#FFF64A',
  styles: [
    {
      featureType: 'all',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'all',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#d4ffc7',
        },
      ],
    },
    {
      featureType: 'all',
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#297110',
        },
      ],
    },
    {
      featureType: 'administrative.province',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#ffffff',
        },
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'geometry.fill',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#508d30',
        },
      ],
    },
    {
      featureType: 'landscape',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape.man_made',
      elementType: 'geometry.fill',
      stylers: [
        {
          visibility: 'off',
        },
        {
          color: '#378f19',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      elementType: 'all',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'landscape.natural',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#508d30',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'all',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry.fill',
      stylers: [
        {
          visibility: 'on',
        },
        {
          color: '#297110',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#75572e',
        },
      ],
    },
    {
      featureType: 'road.highway.controlled_access',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry',
      stylers: [
        {
          color: '#7ea85c',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'geometry',
      stylers: [
        {
          color: '#7ea85c',
        },
      ],
    },
    {
      featureType: 'transit',
      elementType: 'all',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#3b3bea',
        },
      ],
    },
  ],
}

// Satellite by Google
const SATELLITE: MapTheme = {
  id: 109,
  previewImageUrl: '',
  markerColor: '#FF1000',
  markerColors: {
    stroke: '212121', // #212121
    fill: MARKER_FILL_SET_MELLOW,
  },
  connectorLineColor: '#FFF64A',
  labelTextColor: '#000000',
  labelBackgroundColor: '#FFF64A',
  mapTypeId: 'satellite',
  styles: THE_USUAL.styles,
}

// Watercolor by Stamen
const WATERCOLOR: MapTheme = {
  id: 110,
  previewImageUrl: '',
  markerColor: '#FF1000',
  markerColors: {
    stroke: '212121', // #212121
    fill: {
      ...MARKER_FILL_SET_MELLOW,
      default: 'FFF', // #FFFFFF
    },
  },
  mapTypeId: 'watercolor',
  connectorLineColor: '#000000',
  labelTextColor: '#FFFFFF',
  labelBackgroundColor: '#000000',
  styles: THE_USUAL.styles,
}

// THE_USUAL.previewImageUrl = createPreviewImageHash(THE_USUAL)
// SILVER.previewImageUrl = createPreviewImageHash(SILVER)
// BLACK_METAL.previewImageUrl = createPreviewImageHash(BLACK_METAL)
// VINTAGE.previewImageUrl = createPreviewImageHash(VINTAGE)
// RETRO.previewImageUrl = createPreviewImageHash(RETRO)
// TRON.previewImageUrl = createPreviewImageHash(TRON)
// NIGHT.previewImageUrl = createPreviewImageHash(NIGHT)
// AMUSEMENT_PARK.previewImageUrl = createPreviewImageHash(AMUSEMENT_PARK)
// VIDEO_GAME.previewImageUrl = createPreviewImageHash(VIDEO_GAME)
// SATELLITE.previewImageUrl = createPreviewImageHash(SATELLITE)
// WATERCOLOR.previewImageUrl = createPreviewImageHash(WATERCOLOR)

const ALL_THEMES: Record<ValidThemeId, MapTheme> = {
  100: THE_USUAL,
  101: SILVER,
  102: BLACK_METAL,
  103: VINTAGE,
  104: RETRO,
  105: TRON,
  106: NIGHT,
  107: AMUSEMENT_PARK,
  108: VIDEO_GAME,
  109: SATELLITE,
  110: WATERCOLOR,
}

enum ColorScheme {
  light = 'light',
  dark = 'dark',
}

const COLOR_SCHEME: Record<ValidThemeId, ColorScheme> = {
  100: ColorScheme.light,
  101: ColorScheme.light,
  102: ColorScheme.dark,
  103: ColorScheme.light,
  104: ColorScheme.light,
  105: ColorScheme.dark,
  106: ColorScheme.dark,
  107: ColorScheme.light,
  108: ColorScheme.light,
  109: ColorScheme.light,
  110: ColorScheme.light,
}

type ValidThemeId = 100 | 101 | 102 | 103 | 104 | 105 | 106 | 107 | 108 | 109 | 110

const THEME_IDS: ValidThemeId[] = [100, 101, 102, 103, 104, 105, 106, 107, 108, 109, 110]

function getCompatibleThemeId(themeId: number | string): ValidThemeId {
  const numberThemeId = Number(themeId) as ValidThemeId
  if (THEME_IDS.includes(numberThemeId)) {
    return numberThemeId
  }
  return THEME_IDS[0]
}

function getThemeFromThemeId(themeId: number | string): MapTheme {
  return ALL_THEMES[getCompatibleThemeId(themeId)]
}

function getStylesFromThemeId(themeId: number | string): MapTypeStyle[] {
  return getThemeFromThemeId(themeId).styles || []
}

function getMapTypeIdFromThemeId(themeId: number | string): MapTypeId {
  return ((getThemeFromThemeId(themeId).mapTypeId || 'roadmap') as unknown) as MapTypeId
}

// function getPreviewImageUrlFromThemeId (themeId: number | string): string {
//   const validThemeId = getCompatibleThemeId(themeId)
//   const latitude = 37.7376948
//   const longitude = -122.2327052
//   const zoom = 10
//   if (validThemeId === 110) {
//     return 'https://stamen-tiles.a.ssl.fastly.net/watercolor/10/163/395.jpg'
//   }
//   return getThemeFromThemeId(themeId).previewImageUrl
//     .replace('{LATITUDE},{LONGITUDE}', `${latitude},${longitude}`)
//     .replace('{ZOOM}', String(zoom))
// }

function getColorSchemeFromThemeId(themeId: number | string): string {
  return COLOR_SCHEME[getCompatibleThemeId(themeId)]
}

function getMarkerColorsFromThemeId(themeId: number | string): MarkerColorsHash {
  return getThemeFromThemeId(themeId).markerColors
}

function getConnectorColorsFromThemeId(
  themeId: number | string,
): {
  connectorLineColor: string
  labelBackgroundColor: string
  labelTextColor: string
} {
  const theme = getThemeFromThemeId(themeId)
  return {
    connectorLineColor: theme.connectorLineColor || '#000',
    labelBackgroundColor: theme.labelBackgroundColor || '#000',
    labelTextColor: theme.labelTextColor || '#FFF',
  }
}
export {
  ValidThemeId,
  MapTheme,
  // helpers
  getCompatibleThemeId,
  getThemeFromThemeId,
  getStylesFromThemeId,
  getMapTypeIdFromThemeId,
  getMarkerColorsFromThemeId,
  // getPreviewImageUrlFromThemeId,
  getColorSchemeFromThemeId,
  getConnectorColorsFromThemeId,
}
